import type { EntradaMenu } from "@/componentes/layout/menu"
import { tienePermiso } from "@/lib/autorizacion"
import type { UsuarioSesion } from "@/lib/sesion"
import { VISTAS as VISTAS_ADMINISTRACION } from "@/paginas/interno/administracion/Administracion"
import { VISTAS as VISTAS_CUMPLIMIENTO } from "@/paginas/interno/Cumplimiento"
import { VISTAS as VISTAS_LIBRO_CUENTAS } from "@/paginas/interno/LibroCuentas"
import { VISTAS as VISTAS_NOMINA } from "@/paginas/interno/nomina/Nomina"
import { VISTAS as VISTAS_PLANIFICACION } from "@/paginas/interno/planificacion/Planificacion"
import { VISTAS as VISTAS_SUMINISTROS } from "@/paginas/interno/suministros/CadenaSuministros"
import { VISTAS as VISTAS_TALENTO } from "@/paginas/interno/talento/TalentoHumano"
import { VISTAS as VISTAS_TESORERIA } from "@/paginas/interno/tesoreria/Tesoreria"

/**
 * A dónde puede llevar el buscador de módulos (05/11, pedido del usuario): las entradas
 * del menú y las pestañas de cada departamento —«Pagos por ejecutar» es una pestaña de
 * Tesorería, no una entrada del menú—.
 *
 * ⚠ **Nada se copia: las pestañas son las MISMAS `VISTAS` que pinta cada departamento**,
 * con su permiso. Una pestaña nueva sale aquí sola; una lista aparte se quedaría vieja
 * al primer cambio y llevaría a pestañas que ya no existen.
 *
 * ⚠ **Sólo lo que la persona puede abrir**, con la misma regla que la pantalla: la
 * entrada del departamento tiene que estar en SU menú (`GET /menu` ya viene recortado
 * por el servidor) y la pestaña, además, pide su propio permiso. Ofrecer algo que luego
 * dice «no tienes acceso» sería peor que no encontrarlo. El servidor sigue siendo quien
 * decide (`tienePermiso` es cosmético).
 */

export type Destino = {
  ruta: string
  etiqueta: string
  /** Dónde vive: el departamento de una pestaña o la sección de una entrada del menú. */
  donde: string
  /** Otras palabras con las que se la busca («cxp», «sueldos»…). */
  palabras: string
}

type Pestana = {
  valor: string
  etiqueta: string
  permiso?: string
  permisos?: readonly string[]
}

/**
 * Los departamentos cuya pestaña va en la URL (`/tesoreria/pagos`). Los que la llevan
 * en la memoria de la pantalla —Usuarios, Fletes, Retenciones— se encuentran por su
 * entrada del menú: no hay dirección que abra una pestaña suya.
 */
const DEPARTAMENTOS: { base: string; entrada: string; vistas: readonly Pestana[] }[] = [
  { base: "/administracion", entrada: "/administracion", vistas: VISTAS_ADMINISTRACION },
  // La nómina trae su propia fila de pestañas dentro de Administración.
  { base: "/administracion/nomina", entrada: "/administracion", vistas: VISTAS_NOMINA },
  { base: "/tesoreria", entrada: "/tesoreria", vistas: VISTAS_TESORERIA },
  { base: "/libro-cuentas", entrada: "/libro-cuentas", vistas: VISTAS_LIBRO_CUENTAS },
  { base: "/talento-humano", entrada: "/talento-humano", vistas: VISTAS_TALENTO },
  { base: "/planificacion", entrada: "/planificacion", vistas: VISTAS_PLANIFICACION },
  { base: "/cadena-suministros", entrada: "/cadena-suministros", vistas: VISTAS_SUMINISTROS },
  { base: "/cumplimiento", entrada: "/cumplimiento", vistas: VISTAS_CUMPLIMIENTO },
]

/** Lo de cada quien, que no está en el menú: se llega desde el encabezado. */
const PROPIOS: Destino[] = [
  { ruta: "/perfil", etiqueta: "Mi cuenta", donde: "Personal", palabras: "perfil telegram" },
  { ruta: "/avisos", etiqueta: "Avisos", donde: "Personal", palabras: "notificaciones campanita" },
]

/**
 * Cómo lo llama la gente cuando no lo llama por su nombre. Por ruta: si la entrada o
 * la pestaña desaparece, su palabra no lleva a ningún sitio.
 */
const PALABRAS: Record<string, string> = {
  "/tesoreria/pagos": "pagar ordenes aprobadas pendientes",
  "/tesoreria/tasas": "dolar bcv euro usdt cambio",
  "/tesoreria/movimientos": "extracto transacciones",
  "/tesoreria/bancos": "cuentas saldos",
  "/libro-cuentas/por-pagar": "cxp deudas",
  "/libro-cuentas/por-cobrar": "cxc cobranza",
  "/cxp": "cxp",
  "/cxc": "cxc cobranza",
  "/administracion/nomina": "sueldos salarios",
  "/administracion/ordenes-pago": "op",
  "/talento-humano/personas": "empleados trabajadores personal",
  "/admin/usuarios": "roles permisos accesos menu",
  "/credenciales": "claves contrasenas cofre",
  "/drive": "archivos carpetas",
}

function puedeAbrir(usuario: UsuarioSesion | null, pestana: Pestana): boolean {
  if (pestana.permisos) return pestana.permisos.some((p) => tienePermiso(usuario, p))
  return pestana.permiso === undefined || tienePermiso(usuario, pestana.permiso)
}

export function destinosDe(menu: EntradaMenu[], usuario: UsuarioSesion | null): Destino[] {
  const porRuta = new Map<string, Destino>()
  const anadir = (d: Omit<Destino, "palabras">) => {
    if (!porRuta.has(d.ruta)) porRuta.set(d.ruta, { ...d, palabras: PALABRAS[d.ruta] ?? "" })
  }

  for (const e of menu) anadir({ ruta: e.ruta, etiqueta: e.etiqueta, donde: e.seccion })

  const entradas = new Map(menu.map((e) => [e.ruta, e]))
  for (const departamento of DEPARTAMENTOS) {
    const entrada = entradas.get(departamento.entrada)
    if (entrada === undefined) continue
    // La nómina se nombra por su pestaña de Administración: «Nómina · Períodos».
    const padre = VISTAS_ADMINISTRACION.find(
      (v) => `/administracion/${v.valor}` === departamento.base,
    )
    if (padre !== undefined && !puedeAbrir(usuario, padre)) continue
    const donde = padre ? `${entrada.etiqueta} · ${padre.etiqueta}` : entrada.etiqueta
    for (const vista of departamento.vistas) {
      if (!puedeAbrir(usuario, vista)) continue
      anadir({ ruta: `${departamento.base}/${vista.valor}`, etiqueta: vista.etiqueta, donde })
    }
  }

  for (const propio of PROPIOS) if (!porRuta.has(propio.ruta)) porRuta.set(propio.ruta, propio)
  return [...porRuta.values()]
}
